import { ApiClient } from '@/api/client';
import {
  accountMembersClient,
  accountTagCategoriesClient,
  accountTagsClient,
} from '@/api/clients/index';
import { isBoolean } from 'lodash-es';

const client = new ApiClient('/api/v1');

const datePresetOptions = [
  {
    value: 'today',
    label: 'Today',
  },
  {
    value: 'yesterday',
    label: 'Yesterday',
  },
  {
    value: 'this_week',
    label: 'This week',
  },
  {
    value: 'this_month',
    label: 'This month',
  },
  {
    value: 'this_year',
    label: 'This year',
  },
  {
    value: 'last_week',
    label: 'Last week',
  },
  {
    value: 'last_month',
    label: 'Last month',
  },
  {
    value: 'last_year',
    label: 'Last year',
  },
];

const getFilterOptions = (accountId) => {
  if (!accountId) {
    return [];
  }

  return [
    {
      name: 'assignee',
      label: 'Assignee',
      fetchOptions: async () => {
        try {
          const response = await accountMembersClient.list(accountId);

          return Promise.resolve({
            data: (response?.data?.data || []).map((item) => ({
              value: item.id,
              label: item.fullName,
            })),
            meta: response?.data?.meta || null,
          });
        } catch (error) {
          return Promise.reject(error);
        }
      },
      getMessage: async (value, cb) => {
        if (isBoolean(value)) {
          cb(value);
        }

        let ids = value;

        if (typeof ids === 'string') {
          ids = ids.split(',');
        }

        if (Array.isArray(ids) === false) {
          ids = [ids];
        }

        try {
          const response = await accountMembersClient.list(accountId);

          const labels = ids.map((id) => {
            const member = response.data.data.find((memberItem) => memberItem.id === id);

            return member ? member.fullName : id;
          });

          return Promise.resolve(cb(labels));
        } catch (error) {
          return Promise.resolve(cb(value));
        }
      },
      filters: [
        {
          name: 'has_assignee',
          label: "Doesn't have assignee",
          input: 'none',
          value: false,
          message: () => "Doesn't have **assignee**",
        },
        {
          name: 'assignee_is',
          label: 'Assignee is',
          input: 'chips',
          value: null,
          message: (value) => `**assignee** is **${value}**`,
        },
        {
          name: 'assignee_is_not',
          label: 'Assignee is not',
          input: 'chips',
          value: null,
          message: (value) => `**assignee** is not **${value}**`,
        },
        {
          name: 'assignee_in',
          label: 'Assignee is one of',
          input: 'chips:multiple',
          value: [],
          message: (value) => `**assignee** is one of **${value.join(', ')}**`,
        },
        {
          name: 'assignee_not_in',
          label: 'Assignee is not one of',
          input: 'chips:multiple',
          value: [],
          message: (value) => `**assignee** is not one of **${value.join(', ')}**`,
        },
      ],
    },
    {
      name: 'pipelineStage',
      label: 'Pipeline Stage',
      fetchOptions: async () => {
        try {
          const response = await client.get(`accounts/${accountId}/distinct-pipeline-stage-names`);

          return Promise.resolve({
            data: (response?.data?.data || []).map((item) => ({
              value: item.name,
              label: item.name,
            })),
            meta: response?.data?.meta || null,
          });
        } catch (error) {
          return Promise.reject(error);
        }
      },
      getMessage: async (value, cb) => {
        if (isBoolean(value)) {
          cb(value);
        }

        let names = value;

        if (typeof names === 'string') {
          names = names.split(',');
        }

        if (Array.isArray(names) === false) {
          names = [names];
        }

        try {
          const response = await client.get(`accounts/${accountId}/distinct-pipeline-stage-names`);

          const labels = names.map((name) => {
            const pipelineStage = response.data.data.find(
              (pipelineStageItem) => pipelineStageItem.name === name
            );

            return pipelineStage ? pipelineStage.name : name;
          });

          return Promise.resolve(cb(labels));
        } catch (error) {
          return Promise.resolve(cb(value));
        }
      },
      filters: [
        {
          name: 'has_pipeline_stage',
          label: "Doesn't have pipeline stage",
          input: 'none',
          value: false,
          message: () => "Doesn't have **pipeline stage**",
        },
        {
          name: 'pipeline_stage_name_is',
          label: 'Pipeline stage name is',
          input: 'chips',
          value: null,
          message: (value) => `**pipeline stage** is **${value}**`,
        },
        {
          name: 'pipeline_stage_name_is_not',
          label: 'Pipeline stage name is not',
          input: 'chips',
          value: null,
          message: (value) => `**pipeline stage** is not **${value}**`,
        },
        {
          name: 'pipeline_stage_name_in',
          label: 'Pipeline stage name is one of',
          input: 'chips:multiple',
          value: [],
          message: (value) => `**pipeline stage** is one of **${value.join(', ')}**`,
        },
        {
          name: 'pipeline_stage_name_not_in',
          label: 'Pipeline stage name is not one of',
          input: 'chips:multiple',
          value: [],
          message: (value) => `**pipeline stage** is not one of **${value.join(', ')}**`,
        },
      ],
    },
    {
      name: 'tag',
      label: 'Tag',
      fetchOptions: async () => {
        try {
          const response = await accountTagCategoriesClient.list(accountId, {
            filter: {
              taggable_by: 'contact_requests',
            },
            include: 'tags',
            page_size: 500,
          });

          return Promise.resolve({
            data: (response?.data || []).map((category) => ({
              id: category.id,
              label: category.name,
              options: category.tags.map((tag) => ({ value: tag.id, label: tag.name })),
            })),
            meta: response?.meta || null,
          });
        } catch (error) {
          return Promise.reject(error);
        }
      },
      getMessage: async (value, cb) => {
        if (isBoolean(value)) {
          cb(value);
        }

        let ids = value;

        if (typeof ids === 'string') {
          ids = ids.split(',');
        }

        if (Array.isArray(ids) === false) {
          ids = [ids];
        }

        try {
          const response = await accountTagsClient.list(accountId, {
            filter: {
              taggable_by: 'contact_requests',
              id: ids,
            },
          });

          const labels = ids.map((id) => {
            const tag = response.data.find((tagItem) => tagItem.id === id);

            return tag ? tag.name : id;
          });

          return Promise.resolve(cb(labels));
        } catch (error) {
          return Promise.resolve(cb(value));
        }
      },
      filters: [
        {
          name: 'has_tag',
          label: "Doesn't have tag",
          input: 'none',
          value: false,
          message: () => "Doesn't have **tag**",
        },
        {
          name: 'tag_is',
          label: 'Tag is',
          input: 'grouped-chips',
          value: null,
          message: (value) => `**tag** is **${value}**`,
        },
        {
          name: 'tag_is_not',
          label: 'Tag is not',
          input: 'grouped-chips',
          value: null,
          message: (value) => `**tag** is not **${value}**`,
        },
        {
          name: 'tag_in',
          label: 'Tag is one of',
          input: 'grouped-chips:multiple',
          value: [],
          message: (value) => `**tag** is one of **${value.join(', ')}**`,
        },
        {
          name: 'tag_not_in',
          label: 'Tag is not one of',
          input: 'grouped-chips:multiple',
          value: [],
          message: (value) => `**tag** is not one of **${value.join(', ')}**`,
        },
      ],
    },
    {
      name: 'date_created',
      label: 'Date Created',
      fetchOptions: async () => ({
        data: datePresetOptions,
      }),
      getMessage: async (value, cb) => {
        if (isBoolean(value)) {
          cb(value);
        }

        let presetNames = value;

        if (typeof presetNames === 'string') {
          presetNames = presetNames.split(',');
        }

        if (Array.isArray(presetNames) === false) {
          presetNames = [presetNames];
        }

        try {
          const labels = presetNames.map((name) => {
            const preset = datePresetOptions.find((presetItem) => presetItem.value === name);

            return preset ? preset.label : name;
          });

          return Promise.resolve(cb(labels));
        } catch (error) {
          return Promise.resolve(cb(value));
        }
      },
      filters: [
        {
          name: 'date_created_preset',
          label: 'Date created preset',
          input: 'chips',
          value: null,
          message: (value) => `**date created** is **${value}**`,
        },
        {
          name: 'date_created_is',
          label: 'Date created is',
          input: 'date',
          value: null,
          message: (value) => `**date created** is **${value}**`,
        },
        {
          name: 'date_created_is_not',
          label: 'Date created is not',
          input: 'date',
          value: null,
          message: (value) => `**date created** is not **${value}**`,
        },
        {
          name: 'date_created_after',
          label: 'Date created is after',
          input: 'date',
          value: null,
          message: (value) => `**date created** is after **${value}**`,
        },
        {
          name: 'date_created_before',
          label: 'Date created is before',
          input: 'date',
          value: null,
          message: (value) => `**date created** is before **${value}**`,
        },
        {
          name: 'date_created_between',
          label: 'Date created is between',
          input: 'date-range',
          value: [],
          message: (value) =>
            `**date created** is between **${value[0] || 'since'}** and **${value[1] || 'until'}**`,
        },
      ],
    },
  ];
};

export default getFilterOptions;
